/* Cumulative Discounts card (Justin 2026-07-24 mock) — dtf-imgix + uv-by-size-imgix.
 *
 * The legacy pricing tables stay in the DOM as the DATA SOURCE (their engines
 * keep computing tier prices, cumulative counts and the "Add N …" message);
 * this script mirrors that painted data into the new card UI and hides the
 * legacy markup. No pricing math is re-implemented beyond the savings line
 * (unit-price delta × tier minimum).
 *
 * Sources:
 *   bsv4 (dtf-imgix):        .bsv4-pricing rows  [data-bsv4-tier]
 *   bySizeNew (uv-by-size):  custom-table .customTabelPlace rows
 */
(function () {
  'use strict';

  var MONEY = /\$\s*([\d,]+(?:\.\d+)?)/;
  var RANGE = /(\d[\d,]*)\s*[-–—+]\s*(\d[\d,]*)?/;

  function num(s) { return parseFloat(String(s || '').replace(/,/g, '')) || 0; }

  function parseQtyLabel(txt) {
    txt = (txt || '').trim();
    /* strict: needs a range ("15-49") or open top ("250+") — a bare number is
       NOT a tier (it would false-match price cells like "$0.26"). */
    if (/\$/.test(txt)) return null;
    var m = txt.match(RANGE);
    if (m) return { min: num(m[1]), max: m[2] ? num(m[2]) : null };
    return null;
  }

  function parsePct(txt) {
    var m = (txt || '').match(/(\d+(?:\.\d+)?)\s*%/);
    return m ? parseFloat(m[1]) : 0;
  }

  /* ---------- source adapters → { tiers:[{min,max,unit,pct,active}], msg } ---------- */

  function readBsv4(container) {
    var rows = container.querySelectorAll('[data-bsv4-tier]');
    if (!rows.length) return null;
    var tiers = [], msg = '';
    rows.forEach(function (row) {
      var q = parseQtyLabel((row.querySelector('.bsv4-pricing__qty') || {}).textContent);
      if (!q) return;
      var priceTxt = (row.querySelector('[data-bsv4-tier-price]') || {}).textContent || '';
      var pm = priceTxt.match(MONEY);
      var active = row.classList.contains('is-active');
      var msgEl = row.querySelector('[data-bsv4-tier-msg]');
      if (active && msgEl && !msgEl.hidden) msg = msgEl.textContent.trim();
      tiers.push({
        min: q.min, max: q.max,
        unit: pm ? num(pm[1]) : null,
        pct: parsePct((row.querySelector('.bsv4-pricing__off') || {}).textContent),
        active: active
      });
    });
    return tiers.length ? { tiers: tiers, msg: msg } : null;
  }

  function readCustomTable(container) {
    /* Two legacy shapes:
       A) FLAT cells directly under .customTabelPlace — __item_1 (qty),
          __item_2 (discount), __item_3 (price) repeating per tier, plus the
          active row's __msg element.
       B) row wrappers each holding the three cells.
       Group flat cells by walking siblings from each __item_1. */
    var tbl = container.querySelector('.customTabelPlace') || container;
    var tiers = [], msg = '';
    var qtyCells = [].slice.call(tbl.querySelectorAll('[class*="customTabelPlace__item_1"]'));
    if (qtyCells.length) {
      qtyCells.forEach(function (cell) {
        var q = parseQtyLabel(cell.textContent);
        if (!q) return;
        var groupTxt = '', groupCls = String(cell.className || '');
        var sib = cell.nextElementSibling;
        while (sib && !/customTabelPlace__item_1/.test(sib.className || '')) {
          groupTxt += ' ' + (sib.textContent || '');
          groupCls += ' ' + (sib.className || '');
          if (/__msg|message/.test(sib.className || '') && sib.textContent.trim()) msg = sib.textContent.trim();
          sib = sib.nextElementSibling;
        }
        var pm = groupTxt.match(MONEY);
        var active = /active|current|selected/i.test(groupCls);
        if (tiers.some(function (t) { return t.min === q.min; })) return;
        tiers.push({ min: q.min, max: q.max, unit: pm ? num(pm[1]) : null, pct: parsePct(groupTxt), active: active });
      });
    } else {
      var rowEls = tbl.querySelectorAll('.customTabelPlace__item, .customTabelPlace__row, [class*="customTabelPlace__line"]');
      [].forEach.call(rowEls, function (el) {
        var cell1 = el.querySelector('[class*="__item_1"]') || el;
        var q = parseQtyLabel(cell1.textContent);
        if (!q) return;
        var all = el.textContent || '';
        var pm = all.match(MONEY);
        var active = /active|current|is-active|selected/i.test(el.className || '');
        var mEl = el.querySelector('[class*="__msg"], [class*="message"]');
        if (mEl && mEl.textContent.trim()) { active = true; msg = mEl.textContent.trim(); }
        if (tiers.some(function (t) { return t.min === q.min; })) return;
        tiers.push({ min: q.min, max: q.max, unit: pm ? num(pm[1]) : null, pct: parsePct(all), active: active });
      });
    }
    if (!tiers.length) return null;
    tiers.sort(function (a, b) { return a.min - b.min; });
    return { tiers: tiers, msg: msg };
  }

  /* ---------- render ---------- */

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function money(n) { return '$' + n.toFixed(2); }

  function render(host, data) {
    var tiers = data.tiers;
    var activeIdx = -1;
    tiers.forEach(function (t, i) { if (t.active) activeIdx = i; });
    if (activeIdx === -1) activeIdx = 0;
    var active = tiers[activeIdx];
    var next = tiers[activeIdx + 1] || null;

    /* "Add N …" from the engine message; fall back to next.min when absent */
    var need = null;
    var nm = (data.msg || '').match(/Add\s+(\d[\d,]*)/i);
    if (nm) need = num(nm[1]);
    var maxed = !next || /maximum/i.test(data.msg || '');
    var count = null;
    if (!maxed && next) {
      if (need === null || need > next.min) need = Math.max(1, next.min - (active.min - 1) - 1);
      count = Math.max(active.min - 1, next.min - need);
    }

    var headline, countRow, nextLabel;
    if (maxed) {
      headline = 'You’ve unlocked the maximum discount';
      countRow = '<span class="cdxc__count">' + esc(tiers[tiers.length - 1].min) + '+ pieces</span><span class="cdxc__next">max tier reached</span>';
    } else if (count !== null && count > 0) {
      headline = 'You’re ' + need + ' piece' + (need === 1 ? '' : 's') + ' from ' + next.pct + '% off';
      countRow = '<span class="cdxc__count">' + count + ' piece' + (count === 1 ? '' : 's') + '</span><span class="cdxc__next">next tier at ' + next.min + '</span>';
    } else {
      headline = 'Buy more, save up to ' + tiers[tiers.length - 1].pct + '%';
      countRow = '<span class="cdxc__count">0 pieces</span><span class="cdxc__next">next tier at ' + (next ? next.min : '—') + '</span>';
    }

    /* progress: piecewise-linear between tier stops (1 / 15 / 50 / 100 / 250 …) */
    var stops = tiers.map(function (t) { return t.min; });
    var fillPct = 0;
    if (maxed) fillPct = 100;
    else if (count !== null && stops.length > 1) {
      var c = Math.max(count, 0);
      var seg = 0;
      while (seg < stops.length - 1 && c >= stops[seg + 1]) seg++;
      var lo = stops[seg], hi = stops[seg + 1] || (lo * 2);
      var frac = Math.min(1, Math.max(0, (c - lo) / (hi - lo)));
      fillPct = ((seg + frac) / (stops.length - 1)) * 100;
    }

    var scale = stops.map(function (s) { return '<span>' + s + '</span>'; }).join('');

    var rowsHtml = tiers.map(function (t, i) {
      var isActive = i === activeIdx && !maxed || (maxed && i === tiers.length - 1);
      var isNext = next && i === activeIdx + 1 && !maxed;
      var range = t.max ? t.min + '–' + t.max : t.min + '+';
      var badge = isActive
        ? '<span class="cdxc__badge" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg></span>'
        : '<span class="cdxc__badge">' + t.min + '</span>';
      var pctHtml = t.pct ? ' · <span class="cdxc__pct">' + t.pct + '% off</span>' : '';
      var label = range + pctHtml, sub = '';
      if (isActive) sub = '<span class="cdxc__tier-sub">You’re here</span>';
      var price = t.unit != null ? money(t.unit) : '';
      return '<div class="cdxc__tier' + (isActive ? ' cdxc__tier--active' : '') + (isNext ? ' cdxc__tier--next' : '') + '">' +
        badge +
        '<span class="cdxc__tier-text"><span class="cdxc__tier-label">' + label + '</span>' + sub + '</span>' +
        (price ? '<span class="cdxc__tier-price">' + price + '</span>' : '') +
        '</div>';
    }).join('');

    host.innerHTML =
      '<div class="cdxc">' +
        '<div class="cdxc__head">' +
          '<p class="cdxc__eyebrow">Cumulative Discounts</p>' +
          '<h3 class="cdxc__headline">' + esc(headline) + '</h3>' +
          '<p class="cdxc__sub">Cumulative across every transfer and sticker in your order.</p>' +
        '</div>' +
        '<div class="cdxc__body">' +
          '<div class="cdxc__countrow">' + countRow + '</div>' +
          '<div class="cdxc__bar"><span class="cdxc__fill" style="width:' + fillPct.toFixed(1) + '%"></span></div>' +
          '<div class="cdxc__scale">' + scale + '</div>' +
          '<div class="cdxc__tiers">' + rowsHtml + '</div>' +
          '<p class="cdxc__foot">★ <b>Perfect Prints</b> · Free Art Review · No Extra Fees</p>' +
        '</div>' +
      '</div>';
  }

  /* ---------- boot per surface ---------- */

  function attach(watchEl, readFn, hostParent, hideEls) {
    var host = document.createElement('div');
    host.className = 'cdxc-host';
    hostParent.insertBefore(host, hostParent.firstChild);

    var lastJson = '';
    var hideAll = function () {
      hideEls().forEach(function (el) {
        if (el !== host && !host.contains(el) && el.style.display !== 'none') {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    };
    var t = null;
    var apply = function () {
      t = null;
      var data = readFn(watchEl);
      if (!data) return;
      var json = JSON.stringify(data);
      if (json === lastJson) return;
      lastJson = json;
      render(host, data);
      /* hide the legacy table only once the card has real content — if parsing
         ever fails, the shopper still sees the original pricing table. */
      hideAll();
    };
    apply();
    new MutationObserver(function (records) {
      /* ignore our own re-renders; re-hide anything the engine re-shows */
      var external = records.some(function (r) { return !host.contains(r.target) && r.target !== host; });
      if (!external) return;
      if (lastJson) hideAll();
      if (!t) t = setTimeout(apply, 120);
    }).observe(watchEl, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'hidden'] });
  }

  /* Multi-Upload modal rail (Justin 2026-07-25 mock): the engine clones the
     page tier list into [data-bsv4-pricing-rail] lazily and keeps projecting
     prices into the clone — attach the same card UI on top of it there. */
  function bootRail() {
    var railHost = document.querySelector('[data-bsv4-pricing-rail]');
    if (!railHost) return;
    var tryAttach = function () {
      if (railHost.getAttribute('data-cdxc')) return true;
      var wrap = railHost.querySelector('.bsv4-pricing');
      if (!wrap) return false;
      railHost.setAttribute('data-cdxc', '1');
      attach(wrap, readBsv4, railHost, function () { return [wrap]; });
      return true;
    };
    if (tryAttach()) return;
    var mo = new MutationObserver(function () { if (tryAttach()) mo.disconnect(); });
    mo.observe(railHost, { childList: true });
  }

  function boot() {
    var bsv4 = document.querySelector('[data-bsv4-block="price_table"] .bsv4-pricing');
    if (bsv4) {
      attach(bsv4, readBsv4, bsv4.parentNode, function () { return [bsv4]; });
    }
    bootRail();
    var ct = document.querySelector('custom-table');
    if (ct) {
      /* host lives INSIDE custom-table so the flex slot keeps its width; the
         legacy children keep painting (hidden) as the data source. */
      attach(ct, readCustomTable, ct, function () { return [].slice.call(ct.children); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
